import { useState, useEffect } from 'react';
import { XMarkIcon, PhotoIcon } from '@heroicons/react/24/outline';
import { supabase } from '../../lib/supabase';
import { toast } from 'react-hot-toast';
import type { Course } from '../pages/admin/MobileAdminCourses';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  course: Course | null;
}

type Section = 'basic' | 'ratings' | 'contact' | 'media';

const defaultForm = {
  name: '',
  location: '',
  city: '',
  country: '',
  description: '',
  par: 72,
  holes_count: 18,
  website: '',
  phone: '',
  email: '',
  logo_url: '',
  image_url: '',
  established_year: '',
  slope_rating: '',
  course_rating: '',
  is_active: true,
};

export default function MobileCourseSheet({ isOpen, onClose, course }: Props) {
  const [form, setForm] = useState(defaultForm);
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState<Section>('basic');

  useEffect(() => {
    if (!isOpen) return;
    setActiveSection('basic');
    if (course) {
      setForm({
        name: course.name,
        location: course.location ?? '',
        city: course.city ?? '',
        country: course.country ?? '',
        description: course.description ?? '',
        par: course.par,
        holes_count: course.holes_count,
        website: course.website ?? '',
        phone: course.phone ?? '',
        email: course.email ?? '',
        logo_url: course.logo_url ?? '',
        image_url: course.image_url ?? '',
        established_year: course.established_year != null ? String(course.established_year) : '',
        slope_rating: course.slope_rating != null ? String(course.slope_rating) : '',
        course_rating: course.course_rating != null ? String(course.course_rating) : '',
        is_active: course.is_active,
      });
    } else {
      setForm(defaultForm);
    }
  }, [isOpen, course]);

  const handleSubmit = async () => {
    if (!form.name.trim()) { toast.error('Course name is required'); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        location: form.location.trim() || null,
        city: form.city.trim() || null,
        country: form.country.trim() || null,
        description: form.description.trim() || null,
        par: Number(form.par),
        holes_count: Number(form.holes_count),
        website: form.website.trim() || null,
        phone: form.phone.trim() || null,
        email: form.email.trim() || null,
        logo_url: form.logo_url.trim() || null,
        image_url: form.image_url.trim() || null,
        established_year: form.established_year ? Number(form.established_year) : null,
        slope_rating: form.slope_rating ? Number(form.slope_rating) : null,
        course_rating: form.course_rating ? Number(form.course_rating) : null,
        is_active: form.is_active,
        updated_at: new Date().toISOString(),
      };

      if (course) {
        const { error } = await supabase.from('courses').update(payload).eq('id', course.id);
        if (error) throw error;
        toast.success('Course updated');
      } else {
        const { error } = await supabase.from('courses').insert(payload);
        if (error) throw error;
        toast.success('Course created');
      }
      onClose();
    } catch (err: unknown) {
      const msg = (err as { message?: string })?.message ?? 'Failed to save course';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const sections: { key: Section; label: string }[] = [
    { key: 'basic', label: 'Basic' },
    { key: 'ratings', label: 'Ratings' },
    { key: 'contact', label: 'Contact' },
    { key: 'media', label: 'Media' },
  ];

  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-t-2xl flex flex-col max-h-[92vh]">
        <div className="flex items-center justify-between px-4 pt-4 pb-3 border-b border-gray-100">
          <h2 className="text-base font-bold text-golf-gray">
            {course ? 'Edit Course' : 'Add Course'}
          </h2>
          <button onClick={onClose} className="p-2 rounded-xl text-gray-400 hover:bg-gray-100">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="flex border-b border-gray-100 px-4">
          {sections.map((s) => (
            <button
              key={s.key}
              onClick={() => setActiveSection(s.key)}
              className={`flex-1 py-2.5 text-xs font-medium border-b-2 transition-colors ${
                activeSection === s.key
                  ? 'border-golf-orange text-golf-orange'
                  : 'border-transparent text-gray-500'
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
          {activeSection === 'basic' && (
            <>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Course Name *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-golf-orange/30 focus:border-golf-orange outline-none"
                  placeholder="e.g. Augusta National"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">City</label>
                  <input
                    type="text"
                    value={form.city}
                    onChange={(e) => setForm({ ...form, city: e.target.value })}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-golf-orange/30 focus:border-golf-orange outline-none"
                    placeholder="City"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Country</label>
                  <input
                    type="text"
                    value={form.country}
                    onChange={(e) => setForm({ ...form, country: e.target.value })}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-golf-orange/30 focus:border-golf-orange outline-none"
                    placeholder="Country"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Address / Location</label>
                <input
                  type="text"
                  value={form.location}
                  onChange={(e) => setForm({ ...form, location: e.target.value })}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-golf-orange/30 focus:border-golf-orange outline-none"
                  placeholder="Full address"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-golf-orange/30 focus:border-golf-orange outline-none resize-none"
                  placeholder="Brief description..."
                />
              </div>
              <div className="flex items-center gap-3">
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.is_active}
                    onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-10 h-6 bg-gray-200 rounded-full peer peer-checked:bg-golf-green peer-checked:after:translate-x-4 after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all" />
                </label>
                <span className="text-sm font-medium text-gray-700">Active Course</span>
              </div>
            </>
          )}

          {activeSection === 'ratings' && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Total Par</label>
                  <input
                    type="number"
                    value={form.par}
                    onChange={(e) => setForm({ ...form, par: Number(e.target.value) })}
                    min={54} max={90}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-golf-orange/30 focus:border-golf-orange outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Holes</label>
                  <select
                    value={form.holes_count}
                    onChange={(e) => setForm({ ...form, holes_count: Number(e.target.value) })}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-golf-orange/30 focus:border-golf-orange outline-none bg-white"
                  >
                    <option value={9}>9 holes</option>
                    <option value={18}>18 holes</option>
                    <option value={27}>27 holes</option>
                    <option value={36}>36 holes</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Course Rating</label>
                  <input
                    type="number"
                    value={form.course_rating}
                    onChange={(e) => setForm({ ...form, course_rating: e.target.value })}
                    step="0.1" min="55" max="80"
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-golf-orange/30 focus:border-golf-orange outline-none"
                    placeholder="e.g. 72.4"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Slope Rating</label>
                  <input
                    type="number"
                    value={form.slope_rating}
                    onChange={(e) => setForm({ ...form, slope_rating: e.target.value })}
                    step="0.5" min="55" max="155"
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-golf-orange/30 focus:border-golf-orange outline-none"
                    placeholder="e.g. 113"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Established Year</label>
                <input
                  type="number"
                  value={form.established_year}
                  onChange={(e) => setForm({ ...form, established_year: e.target.value })}
                  min={1800} max={new Date().getFullYear()}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-golf-orange/30 focus:border-golf-orange outline-none"
                  placeholder="e.g. 1933"
                />
              </div>
            </>
          )}

          {activeSection === 'contact' && (
            <>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Website</label>
                <input
                  type="url"
                  value={form.website}
                  onChange={(e) => setForm({ ...form, website: e.target.value })}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-golf-orange/30 focus:border-golf-orange outline-none"
                  placeholder="https://..."
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Phone</label>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-golf-orange/30 focus:border-golf-orange outline-none"
                  placeholder="+234..."
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-golf-orange/30 focus:border-golf-orange outline-none"
                  placeholder="info@course.com"
                />
              </div>
            </>
          )}

          {activeSection === 'media' && (
            <>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Logo URL</label>
                <input
                  type="url"
                  value={form.logo_url}
                  onChange={(e) => setForm({ ...form, logo_url: e.target.value })}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-golf-orange/30 focus:border-golf-orange outline-none"
                  placeholder="https://..."
                />
                {form.logo_url && (
                  <div className="mt-2 w-14 h-14 rounded-xl border border-gray-200 overflow-hidden">
                    <img src={form.logo_url} alt="Logo" className="w-full h-full object-contain p-1" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                  </div>
                )}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Course Image URL</label>
                <input
                  type="url"
                  value={form.image_url}
                  onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-golf-orange/30 focus:border-golf-orange outline-none"
                  placeholder="https://..."
                />
                {form.image_url ? (
                  <div className="mt-2 w-full h-28 rounded-xl border border-gray-200 overflow-hidden">
                    <img src={form.image_url} alt="Course" className="w-full h-full object-cover" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                  </div>
                ) : (
                  <div className="mt-2 w-full h-20 rounded-xl border-2 border-dashed border-gray-200 flex items-center justify-center">
                    <div className="text-center">
                      <PhotoIcon className="w-5 h-5 text-gray-300 mx-auto" />
                      <p className="text-xs text-gray-400 mt-0.5">No image set</p>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        <div className="px-4 pt-4 pb-24 border-t border-gray-100 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 py-3 rounded-xl text-sm font-medium text-gray-600 bg-gray-100"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="flex-1 py-3 rounded-xl text-sm font-semibold text-white bg-golf-orange disabled:opacity-60"
          >
            {saving ? 'Saving...' : course ? 'Save Changes' : 'Create Course'}
          </button>
        </div>
      </div>
    </div>
  );
}
